import { useEffect, useState, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './LocalEvents.css';
import '../pages/Landing.css';

/* ====================================================================
   Types
   ==================================================================== */

interface LocalEvent {
  name: string;
  date: string;
  time: string | null;
  venue: string | null;
  address: string | null;
  description: string;
  url: string | null;
  category: string | null;
}

interface EventDigest {
  nanoid: string;
  week_of: string;
  city: string;
  state: string | null;
  events: LocalEvent[];
  generated_at: string;
  first_name: string | null;
}

/* ====================================================================
   Helpers
   ==================================================================== */

function formatWeekOf(weekOf: string): string {
  // weekOf is the Monday; weekend is Saturday + Sunday (+5, +6 days).
  // The API may return either a plain 'YYYY-MM-DD' or a full ISO timestamp
  // (Postgres DATE columns get serialized as 'YYYY-MM-DDT00:00:00.000Z'),
  // so only parse the date portion.
  const [y, m, d] = weekOf.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const monday = new Date(y, m - 1, d);
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const satStr = saturday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const sunStr = sunday.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return `${satStr} – ${sunStr}`;
}

const CATEGORY_COLORS: Record<string, string> = {
  Fitness:   '#e8f5e9',
  Music:     '#fce4ec',
  Family:    '#fff3e0',
  Arts:      '#f3e5f5',
  Food:      '#fff8e1',
  Community: '#e3f2fd',
  Sports:    '#e0f2f1',
  Outdoor:   '#f1f8e9',
};

function categoryStyle(category: string | null): React.CSSProperties {
  const bg = (category && CATEGORY_COLORS[category]) || '#f5f5f5';
  return { background: bg };
}

function isExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/* ====================================================================
   Shell
   ==================================================================== */

function EventsShell({ children }: { children: ReactNode }) {
  return (
    <div className="local-events-page">
      <PublicHeader />
      {children}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ====================================================================
   Event Card
   ==================================================================== */

function EventCard({ event }: { event: LocalEvent }) {
  const hasLink = event.url && isExternalUrl(event.url);

  return (
    <div className="le-event-card">
      <div className="le-event-header">
        <div className="le-event-title-row">
          <h3 className="le-event-name">{event.name}</h3>
          {event.category && (
            <span className="le-event-category" style={categoryStyle(event.category)}>
              {event.category}
            </span>
          )}
        </div>
        <div className="le-event-meta">
          {event.date && (
            <span className="le-event-meta-item">
              <svg className="le-meta-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {event.date}{event.time ? ` · ${event.time}` : ''}
            </span>
          )}
          {event.venue && (
            <span className="le-event-meta-item">
              <svg className="le-meta-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.5 4.5 8.5 4.5 8.5s4.5-5 4.5-8.5c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              {event.venue}{event.address ? ` · ${event.address}` : ''}
            </span>
          )}
        </div>
      </div>
      <p className="le-event-description">{event.description}</p>
      {hasLink && (
        <a
          href={event.url!}
          target="_blank"
          rel="noopener noreferrer"
          className="le-event-link"
        >
          View event details
          <svg className="le-link-arrow" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
      )}
    </div>
  );
}

/* ====================================================================
   Page
   ==================================================================== */

export function LocalEventsPage() {
  const { nanoid } = useParams<{ nanoid: string }>();

  const [digest, setDigest] = useState<EventDigest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [nanoid]);

  useEffect(() => {
    if (!nanoid) {
      setError('not_found');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const res = await fetch(`/api/local/${nanoid}`);
        if (cancelled) return;
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (!res.ok) { setError('error'); setLoading(false); return; }
        const json = await res.json();
        setDigest(json.data);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [nanoid]);

  if (loading) {
    return (
      <EventsShell>
        <div className="le-loading">
          <div className="le-loading-spinner" />
          <p>Loading your events…</p>
        </div>
      </EventsShell>
    );
  }

  if (error || !digest) {
    return (
      <EventsShell>
        <div className="le-not-found">
          <div className="le-not-found-icon">📍</div>
          <h2>Events not available</h2>
          <p>
            {error === 'not_found'
              ? 'This events list is no longer available. Text Mosaime to get fresh local event recommendations.'
              : "We couldn't load these events. Please try again."}
          </p>
          <Link to="/" className="btn hero-cta">Go to Mosaime</Link>
        </div>
      </EventsShell>
    );
  }

  const location = [digest.city, digest.state].filter(Boolean).join(', ');
  const weekendLabel = formatWeekOf(digest.week_of);

  return (
    <EventsShell>

      {/* Hero */}
      <header className="le-hero">
        <div className="landing-container le-hero-inner">
          <p className="le-period-label">Weekend Events</p>
          <h1 className="le-title">What's Happening Near You</h1>
          <p className="le-location">{[location, weekendLabel].filter(Boolean).join(' · ')}</p>
          {digest.first_name && (
            <p className="le-greeting">Hi {digest.first_name}, here are some things to check out this weekend.</p>
          )}
        </div>
      </header>

      <div className="landing-container le-body">

        {digest.events.length === 0 ? (
          <div className="le-empty">
            <p>No events were found for this weekend. Check back next week!</p>
          </div>
        ) : (
          <div className="le-event-list">
            {digest.events.map((event, i) => (
              <EventCard key={i} event={event} />
            ))}
          </div>
        )}

        <div className="le-footer-cta">
          <p>Want events tailored to your interests? Reply to your Mosaime text to update your preferences.</p>
        </div>

      </div>
    </EventsShell>
  );
}

export default LocalEventsPage;
